import React, { useState, useContext, useEffect, useImperativeHandle, useRef } from "react";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import { useForm, Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { AccountContext } from "context/account";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiTagInput from "components/SuiTagInput";

import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

import toast from "react-hot-toast";

import { Upload, DatePicker, Tooltip } from "antd";

import api from "services/api";
import moment from "moment";

import {
  resourceCategoryOptions,
  resourceTypeOptions,
  resourceDetailSketchOptions,
} from "utils/data";
import { handleUploadFiles, getStyleImages, dataURLtoFile } from "utils/imageHelper";
import ImageCardWrapper from "components/UpStyled/ImageCardWrapper";
import Checkbox from "@mui/material/Checkbox";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import { nanoid } from "nanoid";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

const AddResourceModal = React.forwardRef(
  (
    {
      visible,
      handleClose,
      handleOk,
      handleRefresh = () => {},
      isEdit = false,
      resource = {},
      type,
      defaultCategory,
      defaultSubCategory,
    },
    ref
  ) => {
    const [resourceFiles, setResourceFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [coverImage, setCoverImage] = useState();
    const [selectedType, setSelectedType] = useState(type);
    const [copiedText, setCopiedText] = useState();
    const [selectedCategory, setSelectedCategory] = useState(
      defaultCategory ? defaultCategory : null
    );

    const uploadRef = useRef(null);

    if (ref) {
      useImperativeHandle(
        ref,
        () => ({
          coverImage,
          setCoverImage,
        }),
        [coverImage]
      );
    }

    const {
      register,
      handleSubmit,
      trigger,
      setValue,
      getValues,
      reset,
      watch,
      control,
      formState: { errors },
    } = useForm();
    useEffect(() => {
      if (isEdit) {
        setCoverImage(resource?.img);
        setResourceFiles(resource?.metadata?.resourceFiles);
        const categoryOpt = resourceCategoryOptions.find((x) => x?.value == resource?.category);
        const typeOpt = resourceTypeOptions.find((x) => x?.value == resource?.type);

        let resetPayload = {
          title: resource?.title,
          category: resource?.type == "internal" ? resource?.category : categoryOpt,
          type: typeOpt,
          is_shared: resource?.is_shared,
        };

        if (categoryOpt?.value == "detail" && resource?.category != "internal") {
          const sbuCatOpt = resourceDetailSketchOptions.find((x) => x.value == defaultSubCategory);
          resetPayload["subcategory"] = sbuCatOpt;
        }
        setSelectedCategory(resource?.category);
        setSelectedType(resource?.type);
        reset(resetPayload);
      } else {
        if (defaultCategory) {
          const categoryOpt = resourceCategoryOptions.find((x) => x?.value == defaultCategory);

          let resetPayload = {
            category: selectedType == "internal" ? resource?.category : categoryOpt,
            is_shared: true,
          };
          if (defaultCategory == "detail" && selectedType != "internal") {
            const sbuCatOpt = resourceDetailSketchOptions.find(
              (x) => x.value == defaultSubCategory
            );
            resetPayload["subcategory"] = sbuCatOpt;
            setSelectedCategory(defaultCategory);
          }

          reset(resetPayload);
        }
      }
    }, [isEdit]);

    const handleAddResource = async (data) => {
      try {
        setUploading(true);
        let img = null;
        if (coverImage) {
          console.log(coverImage);
          const coverImageUploaded = await handleUploadFiles([coverImage], {}, "online_resource"); //file list,config,directory
          if (coverImageUploaded && coverImageUploaded.length > 0) {
            console.log(coverImageUploaded);
            img = coverImageUploaded[0];
          }
        }

        let payload = {
          title: data["title"],
          type: type ? type : data["type"]?.value,
          category: selectedType == "internal" ? data["category"] : data["category"]?.value,
          img: img,
          is_shared: data["is_shared"],
          metadata: {
            ...resource.metadata,
            subcategory: data["subcategory"]?.value,
            resourceFiles: resourceFiles,
          },
        };
        if (isEdit) {
          await api.onlineResource.update(resource?.id, payload);
        } else {
          await api.onlineResource.create(payload);
        }

        setUploading(false);
        toast.success(`${isEdit ? "Update Resource" : "Add new Resource"} successfully!`);
        handleRefresh();
        handleClose();
      } catch (err) {
        console.log(err);
        toast.error(`${isEdit ? "Update Resource" : "Add new Resource"} failed!`);
        setUploading(false);
      }
    };

    const handleUploadImages = (data) => {
      const file = data.file;
      const image = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });

      console.log(image);

      setCoverImage(image);

      // return api.uploads.create([file])
    };

    const handleOnFileChange = async (e) => {
      const fileList = e?.target?.files;
      setUploading(true);
      try {
        const dir = "online_resource/" + nanoid();
        const { data } = await api.uploads.create(fileList, {}, dir);
        const results = data?.uploads || [];
        const resourceFileList = [];
        for (let i = 0; i < results.length; i++) {
          let mimeType = "*/*";
          for (let j = 0; j < fileList.length; j++) {
            if (fileList[j].name == results[i].name) {
              mimeType = fileList[j].type;
            }
          }

          resourceFileList.push({
            url: results[i].url,
            type: mimeType,
            name: results[i].name,
          });
        }
        setResourceFiles((prev) => [...prev, ...resourceFileList]);
        toast.success("Upload successfully!");
        setUploading(false);
      } catch (err) {
        console.log(err);
        toast.error("Upload failed!");

        setUploading(false);
      }
    };

    const handleRemoveResource = (index) => {
      let newResourceFileList = [...resourceFiles];
      newResourceFileList.splice(index, 1);

      setResourceFiles(newResourceFileList);
    };

    return (
      <Dialog
        open={visible}
        onClose={(e, reason) => {
          if (reason != "backdropClick") {
            handleClose();
          }
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="lg"
      >
        <Backdrop sx={{ color: "info", zIndex: (theme) => 99999 }} open={uploading}>
          <CircularProgress color="info" sx={{ mr: 1 }} size={32} />
          <SuiTypography variant="h6" fontWeight="medium" color="info">
            Uploading supporting files...
          </SuiTypography>
        </Backdrop>
        <DialogTitle id="alert-dialog-title">
          {isEdit ? "Edit Resource" : "Add a Resource"}

          <IconButton
            aria-label="close"
            onClick={() => handleClose()}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form
          onSubmit={handleSubmit(handleOk ? handleOk : handleAddResource)}
          style={{ overflow: "auto", maxHeight: "600px" }}
        >
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12} md={6}>
                  <SuiBox>
                    <SuiBox ml={0.5} mb={1}>
                      <SuiTypography variant="button" fontWeight="bold" textTransform="capitalize">
                        Cover Image
                      </SuiTypography>
                    </SuiBox>

                    <SuiBox sx={{ maxWidth: 256, margin: "auto" }}>
                      {coverImage ? (
                        <div style={{ position: "relative" }}>
                          <ImageCardWrapper
                            data={coverImage}
                            mode={"normal"}
                            width={256}
                            height={320}
                            borderRadius="xl"
                            pdfPagination={true}
                          />
                        </div>
                      ) : (
                        <Upload.Dragger
                          name="design-images"
                          showUploadList={false}
                          customRequest={handleUploadImages}
                          accept="image/*, application/pdf"
                          className="rounded-2xl h-[320px]"
                        >
                          <SuiBox
                            sx={{
                              overflow: "hidden",
                              width: "100%",
                              backgroundImage: ({
                                functions: { rgba, linearGradient },
                                palette: { gradients },
                              }) =>
                                `${linearGradient(
                                  rgba(gradients.secondary.main, 0.2),
                                  rgba(gradients.secondary.state, 0.4)
                                )}`,
                              height: "320px",
                              margin: "-16px 0px",
                              boxShadow: ({ boxShadows: { md } }) => md,
                              objectFit: "contain",
                              objectPosition: "center",
                            }}
                            display="flex"
                            alignItems="center"
                            position="relative"
                            minHeight="320px"
                            borderRadius="xl"
                          />
                        </Upload.Dragger>
                      )}
                    </SuiBox>
                    <SuiBox display="flex" justifyContent="center" mt={2}>
                      <Upload
                        name="design-images"
                        showUploadList={false}
                        customRequest={handleUploadImages}
                        accept="image/*, application/pdf"
                      >
                        <SuiButton color="info" variant="gradient">
                          Upload
                        </SuiButton>
                      </Upload>
                    </SuiBox>
                  </SuiBox>
                </Grid>

                <Grid item xs={12} sm={12} md={6}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={12}>
                      <SuiBox
                        display="flex"
                        flexDirection="column"
                        justifyContent="flex-end"
                        height="100%"
                      >
                        <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                          <SuiTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold"
                            required
                            textTransform="capitalize"
                          >
                            Title
                          </SuiTypography>
                        </SuiBox>
                        <SuiInput
                          placeholder="title"
                          required
                          {...register("title", { required: "A title is required!" })}
                        />
                      </SuiBox>
                    </Grid>
                    {type ? null : (
                      <Grid item xs={12} sm={12}>
                        <SuiBox
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                          height="100%"
                        >
                          <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                            <SuiTypography
                              component="label"
                              variant="caption"
                              fontWeight="bold"
                              textTransform="capitalize"
                            >
                              Resource Type
                            </SuiTypography>
                          </SuiBox>
                          <Controller
                            name="type"
                            control={control}
                            render={({ field }) => (
                              <SuiSelect
                                {...field}
                                options={resourceTypeOptions}
                                placeholder="Resource Type"
                                onChange={(opt) => {
                                  setSelectedType(opt?.value);
                                  field.onChange(opt);
                                }}
                              />
                            )}
                          />
                        </SuiBox>
                      </Grid>
                    )}
                    <Grid item xs={12} sm={12}>
                      <SuiBox
                        display="flex"
                        flexDirection="column"
                        justifyContent="flex-end"
                        height="100%"
                      >
                        <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                          <SuiTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold"
                            textTransform="capitalize"
                            required
                          >
                            Category
                          </SuiTypography>
                        </SuiBox>

                        <Controller
                          name="category"
                          control={control}
                          render={({ field }) =>
                            selectedType == "internal" ? (
                              <SuiInput {...field} placeholder="Category" required />
                            ) : (
                              <SuiSelect
                                {...field}
                                options={resourceCategoryOptions}
                                placeholder="Category"
                                required
                                onChange={(e) => {
                                  setSelectedCategory(e.value);
                                  field.onChange(e);
                                }}
                              />
                            )
                          }
                        />
                      </SuiBox>
                    </Grid>
                    {selectedCategory == "detail" ? (
                      <Grid item xs={12} sm={12}>
                        <SuiBox
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                          height="100%"
                        >
                          <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                            <SuiTypography
                              component="label"
                              variant="caption"
                              fontWeight="bold"
                              textTransform="capitalize"
                              required
                            >
                              Sub-Category
                            </SuiTypography>
                          </SuiBox>

                          <Controller
                            name="subcategory"
                            control={control}
                            render={({ field }) => (
                              <SuiSelect
                                {...field}
                                options={resourceDetailSketchOptions}
                                placeholder="Sub-Category"
                                required
                              />
                            )}
                          />
                        </SuiBox>
                      </Grid>
                    ) : null}
                    <Grid item xs={12} sm={12}>
                      <SuiBox
                        display="flex"
                        flexDirection="column"
                        justifyContent="flex-end"
                        height="100%"
                      >
                        <SuiBox mb={1} mx={1.5} lineHeight={0} display="inline-block">
                          <Controller
                            name="is_shared"
                            control={control}
                            render={({ field }) => (
                              <FormGroup>
                                <FormControlLabel
                                  control={<Checkbox {...field} checked={field?.value} />}
                                  label="Share to All"
                                />
                              </FormGroup>
                            )}
                          />
                        </SuiBox>
                      </SuiBox>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                      <SuiBox
                        display="flex"
                        flexDirection="column"
                        justifyContent="flex-end"
                        height="100%"
                      >
                        <SuiBox
                          mb={1}
                          ml={0.5}
                          lineHeight={0}
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <SuiTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold"
                            textTransform="capitalize"
                          >
                            Resource Files
                          </SuiTypography>
                          <SuiButton
                            color="info"
                            size="small"
                            variant="text"
                            onClick={() => uploadRef?.current?.click()}
                          >
                            + Add
                          </SuiButton>
                        </SuiBox>

                        {resourceFiles.map((item, index) => {
                          const FileNameArr = item.name.split("/");
                          return (
                            <SuiBox
                              display="flex"
                              justifyContent="space-between"
                              gap={1}
                              sx={{ px: 1 }}
                            >
                              <SuiBox display="flex" gap={1}>
                                <SuiTypography
                                  variant="body1"
                                  sx={{ wordBreak: "break-word" }}
                                  component="a"
                                  download
                                  href={item.url}
                                  target="_blank"
                                >
                                  {FileNameArr[FileNameArr.length - 1]}
                                </SuiTypography>
                                <Tooltip title="Copy link">
                                  <a
                                    href=""
                                    onClick={(e) => {
                                      e.preventDefault();
                                      navigator.clipboard.writeText(item.url);
                                      setCopiedText(item.url);
                                    }}
                                  >
                                    {copiedText == item.url ? (
                                      <CheckIcon color="success" />
                                    ) : (
                                      <ContentCopyIcon />
                                    )}
                                  </a>
                                </Tooltip>
                              </SuiBox>

                              <CloseIcon
                                color="error"
                                sx={{ cursor: "pointer" }}
                                onClick={() => handleRemoveResource(index)}
                              />
                            </SuiBox>
                          );
                        })}
                      </SuiBox>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <SuiBox
                pt={3}
                px={3}
                display="flex"
                justifyContent="end"
                alignItems={{ xs: "flex-start", sm: "center" }}
                flexDirection={{ xs: "column", sm: "row" }}
              >
                <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                  <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                    <SuiButton variant="gradient" size="medium" onClick={handleClose} color="light">
                      Cancel
                    </SuiButton>
                  </SuiBox>
                </SuiBox>
                <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                  <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                    <SuiButton size="medium" variant="gradient" color="dark" type="submit">
                      {isEdit ? "Save" : "Add"}
                    </SuiButton>
                  </SuiBox>
                </SuiBox>
              </SuiBox>
            </DialogContentText>
          </DialogContent>
        </form>

        <input
          type="file"
          id="imgupload"
          style={{ display: "none" }}
          ref={uploadRef}
          onChange={handleOnFileChange}
          multiple={true}
        />
      </Dialog>
    );
  }
);

export default AddResourceModal;
